import { Switch, Route, useLocation } from "wouter";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useCurrentUser, isPublicNoAuthPath } from "@/hooks/use-current-user";
import { useOrganizationMetadata } from "@/hooks/use-organization-metadata";
import { LoginPage } from "./pages/login";
import { RegisterPage } from "./pages/register";
import ForgotPasswordPage from "./pages/forgot-password";
import ResetPasswordPage from "./pages/reset-password";
import VerifyEmailPage from "./pages/verify-email";
import InviteAcceptPage from "./pages/invite-accept";
import PublicTicketView from "./pages/ticket/[id]";
import WhatsAppTicketRefResolver from "./pages/ticket/WhatsAppTicketRefResolver";
import TicketCardImageRenderer from "./pages/ticket/TicketCardImageRenderer";
import PrivacyPolicy from "./pages/privacy";
import TermsOfService from "./pages/terms";
import FAQ from "./pages/faq";
import ProvisioningPendingPage from "./pages/provisioning-pending";
import ProvisioningFailedPage from "./pages/provisioning-failed";
// SessionManager entfernt - nur Browser-Sessions
import NotFound from "@/pages/not-found";
import Dashboard from "@/pages/dashboard";
import Projects from "@/pages/projects";
import ProjectDetails from "@/pages/project-details";
import Tickets from "@/pages/tickets";
import Settings from "@/pages/settings";
import Calendar from "@/pages/calendar";
import Bauzeitenplan from "@/pages/bauzeitenplan";
import Listen from "@/pages/listen";
import Reports from "@/pages/reports";
import Contractors from "@/pages/contractors";
import NotificationsPage from "@/pages/notifications";
import { RequirePermission } from "@/components/RequirePermission";
import { useWebNotificationToken } from "@/hooks/use-web-notification-token";
import { UserFriendlyErrorHandler } from "@/components/user-friendly-error-handler";
import { AlertProvider } from "@/contexts/AlertContext";
import { LogoLoader } from "@/components/ui/logo-loader";
import { useDeviceTracking } from "@/hooks/use-device-tracking";
import { useUserPreferences } from "@/hooks/use-preferences";
import { useCurrentOrganization } from "@/hooks/use-current-organization";
import { CookieConsentModal } from "@/components/CookieConsentModal";
import {
  initAnalyticsIfConsented,
  trackScreenView,
  setUserIdentity,
  clearUserIdentity,
  getRouteName,
} from "@/lib/analytics";


function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const { user, isLoading, error } = useCurrentUser();
  
  useEffect(() => {
    // Wait until loading is finished to avoid premature redirects on refresh
    if (isLoading) return;

    // If user is authenticated, check email verification
    if (user) {
      // Check if email is verified (default to true for existing users without the field)
      const emailVerified = (user as any)?.emailVerified !== false;
      if (!emailVerified) {
        // Redirect to login with verification message
        setLocation('/login?verification=required');
        return;
      }

      const orgRole = (user as any)?.orgRole;
      const isRestrictedRole = orgRole === 'watcher' || orgRole === 'business_owner';
      if (isRestrictedRole) {
        const allowedExact = new Set(['/projects', '/settings']);
        const isAllowed = allowedExact.has(location) || location.startsWith('/project/');
        if (!isAllowed) {
          setLocation('/projects');
          return;
        }
      }

      const provisioningStatus = (user as any)?.orgProvisioningStatus as
        | 'pending'
        | 'ready'
        | 'failed'
        | undefined;
      const isProvisioningRoute =
        location === '/provisioning-pending' || location === '/provisioning-failed';

      if (provisioningStatus === 'pending' && !isProvisioningRoute) {
        setLocation('/provisioning-pending');
        return;
      }
      if (provisioningStatus === 'failed' && !isProvisioningRoute) {
        setLocation('/provisioning-failed');
        return;
      }
      if (provisioningStatus === 'ready' && (location === '/provisioning-pending' || location === '/provisioning-failed')) {
        setLocation('/dashboard');
        return;
      }
      return;
    }

    // If there's an error or user is not authenticated, check for dev access
    const hasDevAccess = localStorage.getItem('devAccess') === 'true';
    if (!hasDevAccess) {
      setLocation('/login');
      return;
    }
    setLocation('/login');
  }, [user, error, isLoading, setLocation, location]);
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LogoLoader size="lg" />
      </div>
    );
  }

  if (error || !user) {
    return <div>Redirecting to login...</div>;
  }

  // Double-check email verification before rendering (in case user object changed)
  const emailVerified = (user as any)?.emailVerified !== false;
  if (!emailVerified) {
    return <div>Redirecting to login...</div>;
  }

  const provisioningStatus = (user as any)?.orgProvisioningStatus as
    | 'pending'
    | 'ready'
    | 'failed'
    | undefined;
  const isProvisioningRoute =
    location === '/provisioning-pending' || location === '/provisioning-failed';
  if (provisioningStatus === 'pending' && !isProvisioningRoute) {
    return <div>Redirecting to provisioning...</div>;
  }
  if (provisioningStatus === 'failed' && !isProvisioningRoute) {
    return <div>Redirecting to provisioning...</div>;
  }
  if (provisioningStatus === 'ready' && (location === '/provisioning-pending' || location === '/provisioning-failed')) {
    return <div>Redirecting...</div>;
  }

  return <>{children}</>;
}

function Router() {
  return (
    <Switch>
      <Route path="/login" component={LoginPage} />
      <Route path="/register" component={RegisterPage} />
      <Route path="/forgot-password" component={ForgotPasswordPage} />
      <Route path="/reset-password/:token" component={ResetPasswordPage} />
      <Route path="/verify-email/:token" component={VerifyEmailPage} />
      <Route path="/invite/accept" component={InviteAcceptPage} />
      <Route path="/ticket/view/:id" component={PublicTicketView} />
      <Route path="/ticket/:ticketRef" component={WhatsAppTicketRefResolver} />
      <Route path="/ticket-card-image" component={TicketCardImageRenderer} />
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/terms" component={TermsOfService} />
      <Route path="/faq" component={FAQ} />
      <Route path="/" component={LoginPage} />
      <Route path="/dashboard">
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      </Route>
      <Route path="/projects">
        <ProtectedRoute><Projects /></ProtectedRoute>
      </Route>
      <Route path="/project/:id">
        <ProtectedRoute><ProjectDetails /></ProtectedRoute>
      </Route>
      <Route path="/tickets">
        <ProtectedRoute><Tickets /></ProtectedRoute>
      </Route>
      <Route path="/calendar">
        <ProtectedRoute>
          <RequirePermission permission={['manage_projects', 'manage_all_projects']}>
            <Calendar />
          </RequirePermission>
        </ProtectedRoute>
      </Route>
      <Route path="/bauzeitenplan">
        <ProtectedRoute><Bauzeitenplan /></ProtectedRoute>
      </Route>
      <Route path="/listen">
        <ProtectedRoute><Listen /></ProtectedRoute>
      </Route>
      <Route path="/reports">
        <ProtectedRoute><Reports /></ProtectedRoute>
      </Route>
      <Route path="/contractors">
        <ProtectedRoute><Contractors /></ProtectedRoute>
      </Route>
      <Route path="/settings">
        <ProtectedRoute><Settings /></ProtectedRoute>
      </Route>
      <Route path="/provisioning-pending">
        <ProtectedRoute><ProvisioningPendingPage /></ProtectedRoute>
      </Route>
      <Route path="/provisioning-failed">
        <ProtectedRoute><ProvisioningFailedPage /></ProtectedRoute>
      </Route>
      <Route path="/notifications">
        <ProtectedRoute><NotificationsPage /></ProtectedRoute>
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
}

// Component to initialize web push notifications when authenticated
function WebPushInitializer() {
  const { user } = useCurrentUser();
  // This hook is guarded - it only runs if Firebase config is available
  useWebNotificationToken(!!user);
  return null;
}

// Consent-gated GA4 analytics: init when cookies preference is true, track route and user identity
function AnalyticsInitializer() {
  const [location] = useLocation();
  const { user } = useCurrentUser();
  const { data: preferences } = useUserPreferences({
    enabled: !isPublicNoAuthPath(location) && !!user,
  });
  const { organizationId } = useCurrentOrganization();

  // Sync consent: only enable when user is logged in and preferences.cookies is true
  useEffect(() => {
    const consent = !!(user && preferences && preferences.cookies);
    initAnalyticsIfConsented(consent);
  }, [user, preferences]);

  // Set or clear user identity for analytics
  useEffect(() => {
    if (user) {
      const orgId = organizationId ?? undefined;
      const role = (user as any)?.orgRole ?? (user as any)?.role ?? undefined;
      setUserIdentity((user as any).id, orgId, role);
    } else {
      clearUserIdentity();
    }
  }, [user, organizationId]);

  // Track screen view on route change
  useEffect(() => {
    trackScreenView(location, getRouteName(location));
  }, [location]);

  return null;
}

function App() {
  // Preload organization metadata (status and priorities) when user is authenticated
  // This ensures data is cached globally and available to all components
  // The hook itself handles the case when user is not authenticated
  useOrganizationMetadata();
  
  // Track device access on app initialization (for authenticated users)
  // This will register the device automatically when user is logged in
  useDeviceTracking('page_view');
  
  return (
    <TooltipProvider>
      <AlertProvider>
        <UserFriendlyErrorHandler />
        <Toaster />
        <WebPushInitializer />
        <AnalyticsInitializer />
        <CookieConsentModal />
        <Router />
      </AlertProvider>
    </TooltipProvider>
  );
}

export default App;
